/* eslint-disable no-fallthrough */
/* eslint-disable no-console */
// In production, we register a service worker to serve assets from local cache.

import { diff } from "semver";

import { appUpdateStatus, appUpdateType } from "constants/system";

import { debugServiceWorkerLog } from "lib/errors";

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on the "N+1" visit to a page, since previously
// cached resources are updated in the background.

// To learn more about the benefits of this model, read https://goo.gl/KwvDNy.
// This link also includes instructions on opting out of this behavior.

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);

export function checkForSWUpdates() {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      console.log("Checking for update");
      registration.update().then(() => {
        console.log("Finished update");
      });
    });
  });
}

function registerValidSW(swUrl, onUpdate) {
  debugServiceWorkerLog("registerValidSW called");
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      debugServiceWorkerLog("setting interval");
      // eslint-disable-next-line no-param-reassign
      const updateChecker = setInterval(() => {
        console.log("Checking for updates...");
        registration.update().then(
          () => console.log("finished checking"),
          e => {
            console.log(`Update check failed: ${e}`);
            if (e.msg === "Not found") {
              clearInterval(updateChecker);
            }
          },
        );
      }, process.env.REACT_APP_UPDATE_CHECK_MS || 300000); // Default check for updates every 5 minutes

      registration.onupdatefound = async () => {
        debugServiceWorkerLog("registration.onupdatefound called");
        const installingWorker = registration.installing;
        installingWorker.onstatechange = async () => {
          if (installingWorker.state === "installed") {
            debugServiceWorkerLog("service worker installed");
            if (navigator.serviceWorker.controller) {
              debugServiceWorkerLog("service worker controller found");
              // At this point, the old content will have been purged and
              // the fresh content will have been added to the cache.
              // It's the perfect time to display a "New content is
              // available; please refresh." message in your web app

              const currentVersion = process.env.REACT_APP_SEMANTIC_VERSION;
              console.log(`Current app version: ${currentVersion}`);

              // According to workbox documentation we should be able to access the pre-cache
              // and in theory the new version of the app:
              // https://developer.chrome.com/docs/workbox/modules/workbox-precaching#reading_precached_assets_directly
              // However this did not work - cacheNames.precache => "workbox-precache-v2" which empty at this point
              // There is another caches which is populated "workbox-precache-<origin>" but that has stale data, i.e.
              // it stores the previous (current) version.  So instead we will just fetch the new version file directly

              let nextVersion = null;
              const fetchedVersionResponse = await fetch("/version.json");
              const fetchedVersionObject = await fetchedVersionResponse.json();
              nextVersion = fetchedVersionObject.version;

              let updateType = appUpdateType.PATCH; // default release type
              if (nextVersion) {
                console.log(`Fetched next app version: ${nextVersion}`);
                if (currentVersion) {
                  updateType = diff(currentVersion, nextVersion) || updateType;
                }
              } else {
                console.log("Could not fetch next app version");
              }

              console.log(`Update type: ${updateType}`);

              let updateStatus = null;

              switch (updateType) {
                // The default production release type
                case appUpdateType.PATCH: // e.g. 1.0.1 => 1.0.2
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Pre-release updates for non-prod e.g. sandbox will have this type
                case appUpdateType.PRERELEASE: // e.g. 1.0.1-pre.956ddcc7a => 1.0.1-pre.bcd4e2a82
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Invisible release for urgent fixes that we don't want the world to know about
                // Users will need to be be informed by support and instructed to manually refresh
                case appUpdateType.PREPATCH: // e.g. 1.0.1 => 1.0.2-pre.fixplacards
                  updateStatus = appUpdateStatus.INVISIBLE;
                  break;

                // Pre-release updates for non-prod e.g. sandbox will have this type, if we incremented
                // the minor version on the pre-release
                case appUpdateType.PREMINOR: // e.g. 2.0.1 => 2.1.0-pre.f11ea25ea
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Production minor release with in your face toast popup notification
                case appUpdateType.MINOR: // e.g. 2.0.1 => 2.1.0
                  updateStatus = appUpdateStatus.REFRESH_REQUIRED;
                  break;

                // Pre-release updates for non-prod e.g. sandbox will have this type, if we incremented
                // the minor version on the pre-release
                case appUpdateType.PREMAJOR: // e.g. 2.0.1 => 3.0.0-pre.f11ea25ea
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
                  break;

                // Production major forces user logout
                case appUpdateType.MAJOR: // e.g. 2.0.1 => 3.0.0
                  updateStatus = appUpdateStatus.LOGOUT_REQUIRED;
                  break;

                default:
                  updateStatus = appUpdateStatus.REFRESH_OPTIONAL;
              }
              onUpdate(updateStatus);

              console.log("New content is available; please refresh.");
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log("Content is cached for offline use.");
            }
          }
        };
      };
    })
    .catch(error => {
      console.error("Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(swUrl, onUpdate) {
  debugServiceWorkerLog(`checkValidServiceWorker called - swUrl: ${swUrl}`);
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl)
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      if (
        response.status === 404 ||
        response.headers.get("content-type").indexOf("javascript") === -1
      ) {
        debugServiceWorkerLog("no service worker found - reload the page");
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        debugServiceWorkerLog("register valid service worker");
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, onUpdate);
      }
    })
    .catch(() => {
      console.log(
        "No internet connection found. App is running in offline mode.",
      );
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready.then(registration => {
      registration.unregister();
    });
  }
}

export default function register(onUpdate) {
  if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    console.log("server worker registring");
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location);
    if (publicUrl.origin !== window.location.origin) {
      debugServiceWorkerLog(
        "service worker giving up - public URL origin differs from window origin",
        {
          publicUrlOrign: publicUrl.origin,
          windowLocationOrigin: window.location.origin,
        },
      );
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebookincubator/create-react-app/issues/2374
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `${process.env.PUBLIC_URL}/sw-default.js`;

      // Don't use the service worker for the reports endpoint, these must be online
      // and it helps with sharing the reports on ios pwa.
      if (window.location.pathname.startsWith("/reports")) {
        debugServiceWorkerLog("unregistering service worker for reports url");
        unregister();
        return false;
      }

      if (isLocalhost) {
        debugServiceWorkerLog("service worker isLocalHost");
        // This is running on localhost. Lets check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, onUpdate);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            "This web app is being served cache-first by a service " +
              "worker. To learn more, visit https://goo.gl/SC7cgQ",
          );
        });
      } else {
        debugServiceWorkerLog("registering valid servicer worker");
        // Is not local host. Just register service worker
        registerValidSW(swUrl, onUpdate);
      }
    });
  }
}
